import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Terminal as TerminalIcon, X, Copy, RotateCw, Wifi, WifiOff, RefreshCw, Upload, Sparkles, Send, FolderTree } from 'lucide-react';
import { Terminal } from 'xterm';
import { FitAddon } from '@xterm/addon-fit';
import 'xterm/css/xterm.css';
import '../styles/terminal.css';
import api from '../services/api';
import ErrorBoundary from './ErrorBoundary';

const MAX_RECONNECT_ATTEMPTS = 8;
const HEARTBEAT_INTERVAL = 15000;
const HEARTBEAT_TIMEOUT = 10000;
const BASE_RECONNECT_DELAY = 1000;

const ContainerTerminal = ({ appName, owner, token, mode = 'shell', command = '/bin/sh', sshConfig = null, onClose, onStatusMessage, onOpenFileManager, embedded = false }) => {
    const terminalRef = useRef(null);
    const wsRef = useRef(null);
    const terminalInstanceRef = useRef(null);
    const fitAddonRef = useRef(null);
    const sessionCreatedRef = useRef(false);
    const onStatusMessageRef = useRef(onStatusMessage);
    const fileInputRef = useRef(null);
    const [sessionId, setSessionId] = useState(null);
    const [error, setError] = useState(null);
    const reconnectAttemptsRef = useRef(0);
    const reconnectTimerRef = useRef(null);
    const [connectionState, setConnectionState] = useState('initializing');
    const [latency, setLatency] = useState(null);
    const heartbeatRef = useRef(null);
    const pongTimeoutRef = useRef(null);
    const lastPingRef = useRef(null);
    const isDisposedRef = useRef(false);
    const [uploading, setUploading] = useState(false);
    const resolvedMode = String(mode || 'shell').trim() === 'ssh' ? 'ssh' : 'shell';
    const resolvedCommand = String(command || '/bin/sh').trim() || '/bin/sh';
    const resolvedSSHConfig = React.useMemo(() => sshConfig && typeof sshConfig === 'object' ? {
        host: String(sshConfig.host || '').trim(),
        port: String(sshConfig.port || '22').trim(),
        user: String(sshConfig.user || '').trim(),
        key: String(sshConfig.key || '').trim()
    } : null, [sshConfig]);

    // AI state
    const [showAi, setShowAi] = useState(false);
    const [aiMessages, setAiMessages] = useState([]);
    const [aiInput, setAiInput] = useState('');
    const [aiLoading, setAiLoading] = useState(false);
    const aiMessagesEndRef = useRef(null);
    const terminalOutputRef = useRef('');

    useEffect(() => {
        onStatusMessageRef.current = onStatusMessage;
    }, [onStatusMessage]);

    // Auto-scroll AI messages
    useEffect(() => {
        if (aiMessagesEndRef.current) {
            const container = aiMessagesEndRef.current.closest('.ai-messages');
            if (container) {
                const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
                if (isNearBottom || aiMessages.length <= 1) {
                    aiMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            } else {
                aiMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [aiMessages, aiLoading]);

    // Refit terminal when AI panel toggles
    useEffect(() => {
        if (fitAddonRef.current && terminalRef.current) {
            setTimeout(() => {
                if (terminalRef.current && terminalRef.current.clientWidth > 0 && terminalRef.current.clientHeight > 0) {
                    try { fitAddonRef.current.fit(); } catch (e) { /* ignore */ }
                }
            }, 300);
        }
    }, [showAi]);

    const clearHeartbeat = useCallback(() => {
        if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
        if (pongTimeoutRef.current) { clearTimeout(pongTimeoutRef.current); pongTimeoutRef.current = null; }
    }, []);

    const startHeartbeat = useCallback((ws) => {
        clearHeartbeat();
        heartbeatRef.current = setInterval(() => {
            if (ws && ws.readyState === WebSocket.OPEN) {
                lastPingRef.current = Date.now();
                try { ws.send(JSON.stringify({ type: 'ping', ts: lastPingRef.current })); } catch (e) { }
                pongTimeoutRef.current = setTimeout(() => {
                    if (!isDisposedRef.current && ws.readyState === WebSocket.OPEN) {
                        ws.close(4000, 'Heartbeat timeout');
                    }
                }, HEARTBEAT_TIMEOUT);
            }
        }, HEARTBEAT_INTERVAL);
    }, [clearHeartbeat]);

    const createSession = useCallback(async () => {
        try {
            setConnectionState('initializing');
            setError(null);
            const data = await api.createTerminalSession(appName, owner, {
                mode: resolvedMode, command: resolvedCommand, sshConfig: resolvedSSHConfig
            });
            setSessionId(data.sessionId);
            return data.sessionId;
        } catch (err) {
            setError(err.message);
            setConnectionState('error');
            onStatusMessageRef.current?.(`Terminal error: ${err.message}`, 'error');
            return null;
        }
    }, [appName, owner, resolvedCommand, resolvedMode, resolvedSSHConfig]);

    useEffect(() => {
        if (sessionCreatedRef.current) return;
        sessionCreatedRef.current = true;
        createSession();
        return () => {
            isDisposedRef.current = true;
            clearHeartbeat();
            if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) wsRef.current.close();
        };
    }, [appName, createSession, clearHeartbeat]);

    useEffect(() => {
        if (!sessionId || !terminalRef.current) return;
        let isDisposed = false;
        let term = null;
        let fitAddon = null;
        let ws = null;
        let resizeTimeout = null;
        let initTimer = null;
        let rafId = null;
        let resizeObserver = null;

        const safeFit = () => {
            if (isDisposed || !fitAddon || !terminalRef.current) return;
            if (terminalRef.current.clientWidth <= 0 || terminalRef.current.clientHeight <= 0) return;
            if (!term || !term.element || !term.element.offsetParent) return;
            try { fitAddon.fit(); } catch (e) { }
        };

        const cleanup = () => {
            isDisposed = true;
            clearHeartbeat();
            if (rafId) cancelAnimationFrame(rafId);
            if (initTimer) clearTimeout(initTimer);
            if (resizeTimeout) clearTimeout(resizeTimeout);
            if (resizeObserver) resizeObserver.disconnect();
            window.removeEventListener('resize', handleResize);
            if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) ws.close();
            if (term) { try { term.dispose(); } catch (e) { } }
            terminalInstanceRef.current = null;
            fitAddonRef.current = null;
            wsRef.current = null;
        };

        const handleResize = () => {
            if (isDisposed) return;
            if (resizeTimeout) clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => { rafId = requestAnimationFrame(safeFit); }, 100);
        };

        const attemptReconnect = async () => {
            reconnectAttemptsRef.current += 1;
            const attempt = reconnectAttemptsRef.current;
            if (attempt <= MAX_RECONNECT_ATTEMPTS) {
                setConnectionState('reconnecting');
                const baseDelay = Math.min(30000, BASE_RECONNECT_DELAY * Math.pow(2, attempt - 1));
                const delay = baseDelay + Math.random() * 1000;
                if (term) { try { term.write(`\r\n\x1b[33m[Reconnecting in ${Math.round(delay / 1000)}s... attempt ${attempt}/${MAX_RECONNECT_ATTEMPTS}]\x1b[0m\r\n`); } catch (e) { } }
                reconnectTimerRef.current = setTimeout(async () => {
                    if (isDisposed) return;
                    try { connectWebSocket(); } catch (e) {
                        if (term) { try { term.write(`\r\n\x1b[33m[Session expired, creating new session...]\x1b[0m\r\n`); } catch (e2) { } }
                        const newSessionId = await createSession();
                        if (newSessionId) setSessionId(newSessionId);
                    }
                }, delay);
            } else {
                setConnectionState('disconnected');
                setError('Unable to reconnect to terminal (max attempts reached)');
                if (term) { try { term.write(`\r\n\x1b[31m[Connection lost - max reconnect attempts reached.]\x1b[0m\r\n`); } catch (e) { } }
            }
        };

        const connectWebSocket = () => {
            setConnectionState('connecting');
            const wsUrl = api.getTerminalWsUrl(sessionId);
            ws = new WebSocket(wsUrl);

            ws.onopen = () => {
                if (isDisposed) { ws.close(); return; }
                reconnectAttemptsRef.current = 0;
                setConnectionState('connected');
                setError(null);
                onStatusMessageRef.current?.(`Connected to ${appName} terminal`, 'success');
                startHeartbeat(ws);
                setTimeout(() => {
                    if (!isDisposed && term && ws.readyState === WebSocket.OPEN) {
                        safeFit();
                        const dimensions = fitAddon?.proposeDimensions?.();
                        if (dimensions) {
                            ws.send(JSON.stringify({ type: 'resize', cols: dimensions.cols, rows: dimensions.rows }));
                        }
                    }
                }, 100);
            };

            ws.onmessage = (event) => {
                if (isDisposed || !term) return;
                try {
                    const message = JSON.parse(event.data);
                    switch (message.type) {
                        case 'output':
                            term.write(message.data);
                            // Capture last ~2000 chars of output for AI context
                            terminalOutputRef.current = (terminalOutputRef.current + message.data).slice(-2000);
                            break;
                        case 'connected':
                            term.write(`\r\n\x1b[32m${message.message}\x1b[0m\r\n`);
                            break;
                        case 'pong':
                            if (pongTimeoutRef.current) { clearTimeout(pongTimeoutRef.current); pongTimeoutRef.current = null; }
                            if (lastPingRef.current) setLatency(Date.now() - lastPingRef.current);
                            break;
                        case 'exit':
                            term.write(`\r\n\x1b[31m[Process exited with code ${message.code}]\x1b[0m\r\n`);
                            setConnectionState('disconnected');
                            clearHeartbeat();
                            break;
                        case 'error':
                            term.write(`\r\n\x1b[31m[Error: ${message.message}]\x1b[0m\r\n`);
                            setError(message.message);
                            setConnectionState('error');
                            clearHeartbeat();
                            break;
                        default: break;
                    }
                } catch (err) { }
            };

            ws.onerror = () => { if (!isDisposed) clearHeartbeat(); };
            ws.onclose = (event) => {
                if (isDisposed) return;
                clearHeartbeat();
                setLatency(null);
                if (event.code === 1000) {
                    setConnectionState('disconnected');
                    if (term) { try { term.write('\r\n\x1b[33m[Connection closed]\x1b[0m\r\n'); } catch (e) { } }
                    return;
                }
                attemptReconnect();
            };

            wsRef.current = ws;
            term.onData((data) => { if (!isDisposed && ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'input', data })); });
            term.onResize((size) => { if (!isDisposed && ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'resize', cols: size.cols, rows: size.rows })); });
        };

        const initTerminal = () => {
            if (isDisposed || !terminalRef.current) return;
            if (terminalRef.current.clientWidth <= 0 || terminalRef.current.clientHeight <= 0) { initTimer = setTimeout(initTerminal, 50); return; }
            try {
                term = new Terminal({
                    cursorBlink: true, fontSize: 14,
                    fontFamily: 'Monaco, Menlo, Ubuntu Mono, Consolas, source-code-pro, monospace',
                    scrollback: 5000,
                    theme: {
                        background: '#1e1e1e', foreground: '#d4d4d4', cursor: '#aeafad',
                        selection: 'rgba(255, 255, 255, 0.2)',
                        black: '#000000', red: '#d75f5f', green: '#87af5f', yellow: '#d7af5f',
                        blue: '#87afdf', magenta: '#af87d7', cyan: '#5f87af', white: '#d4d4d4'
                    }
                });
                fitAddon = new FitAddon();
                term.loadAddon(fitAddon);
                term.open(terminalRef.current);
                terminalInstanceRef.current = term;
                fitAddonRef.current = fitAddon;
                rafId = requestAnimationFrame(safeFit);
                window.addEventListener('resize', handleResize);
                if (typeof ResizeObserver !== 'undefined') {
                    resizeObserver = new ResizeObserver(() => handleResize());
                    resizeObserver.observe(terminalRef.current);
                }
                connectWebSocket();
            } catch (err) {
                setError(err.message);
                setConnectionState('error');
                onStatusMessageRef.current?.(`Terminal error: ${err.message}`, 'error');
            }
        };

        initTerminal();
        return cleanup;
    }, [sessionId, appName, createSession, startHeartbeat, clearHeartbeat]);

    const handleCopy = () => {
        const selection = terminalInstanceRef.current?.getSelection();
        if (selection) navigator.clipboard.writeText(selection).then(() => onStatusMessageRef.current?.('Copied to clipboard', 'success'));
    };

    const handleZipUpload = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        e.target.value = '';
        if (!file.name.toLowerCase().endsWith('.zip')) { onStatusMessageRef.current?.('Only .zip files are supported', 'error'); return; }
        setUploading(true);
        const term = terminalInstanceRef.current;
        if (term) term.write(`\r\n\x1b[36m[Uploading ${file.name}...]\x1b[0m\r\n`);
        try {
            const result = await api.uploadZipToContainer(appName, file, owner || null);
            onStatusMessageRef.current?.(result.message || 'ZIP uploaded successfully', 'success');
            if (term) term.write(`\x1b[32m[✓ ${result.message || 'ZIP extracted successfully'}]\x1b[0m\r\n`);
        } catch (err) {
            onStatusMessageRef.current?.(`Upload failed: ${err.message}`, 'error');
            if (term) term.write(`\x1b[31m[✗ Upload failed: ${err.message}]\x1b[0m\r\n`);
        } finally { setUploading(false); }
    };

    const handleClear = () => {
        if (terminalInstanceRef.current && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            terminalInstanceRef.current.clear();
            wsRef.current.send(JSON.stringify({ type: 'input', data: 'clear\r' }));
        }
    };

    const handleReconnect = async () => {
        reconnectAttemptsRef.current = 0;
        setError(null);
        setConnectionState('initializing');
        if (wsRef.current) { try { wsRef.current.close(); } catch (e) { } }
        sessionCreatedRef.current = false;
        const newSessionId = await createSession();
        if (newSessionId) setSessionId(newSessionId);
    };

    // Inject a command into the terminal WebSocket
    const runCommandInTerminal = useCallback((cmd) => {
        const ws = wsRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'input', data: cmd + '\r' }));
        }
    }, []);

    const autoRetriesRef = useRef(0);

    const handleAiSendInner = async (overridePrompt, isAutoRetry = false) => {
        const prompt = overridePrompt || aiInput.trim();
        if (!prompt || aiLoading) return;
        
        if (!isAutoRetry) {
            setAiInput('');
            autoRetriesRef.current = 0;
        }
        
        setAiMessages(prev => [...prev, { role: 'user', content: prompt }]);
        setAiLoading(true);
        try {
            const result = await api.terminalAiAssist(prompt, appName, terminalOutputRef.current.slice(-2000), owner);
            const msg = result.message || 'Done';
            const cmds = Array.isArray(result.commands) ? result.commands : [];
            const isComplete = result.is_complete !== false; // strict check
            
            setAiMessages(prev => [...prev, { role: 'assistant', content: msg, commands: cmds }]);
            
            // Auto-execute commands in terminal intelligently
            if (cmds.length > 0 && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                const beforeOutput = terminalOutputRef.current;
                
                // Wait for commands to actually finish running in the interactive shell
                for (let i = 0; i < cmds.length; i++) {
                    const cmd = cmds[i];
                    const marker = `__AI_DONE_${Date.now()}_${i}__`;
                    
                    // Inject command and echo a unique marker to know when it finishes
                    runCommandInTerminal(`${cmd}; echo "\\r\\n${marker}"`);
                    
                    await new Promise(resolve => {
                        // Max 120 seconds wait per command
                        let timeout = setTimeout(() => resolve('timeout'), 120000);
                        const checkInterval = setInterval(() => {
                            if (terminalOutputRef.current.includes(marker)) {
                                clearTimeout(timeout);
                                clearInterval(checkInterval);
                                resolve('done');
                            }
                        }, 250);
                    });
                }
                
                // Wait an extra half second for terminal buffer to fully settle
                await new Promise(r => setTimeout(r, 500));
                
                if (autoRetriesRef.current >= 6) {
                     setAiMessages(prev => [...prev, { role: 'error', content: 'Agent stopped after 6 automatic steps. You can manually prompt it to continue.' }]);
                     setAiLoading(false);
                     return;
                }
                
                const currentOutput = terminalOutputRef.current;
                let added = currentOutput;
                if (currentOutput.startsWith(beforeOutput)) {
                    added = currentOutput.slice(beforeOutput.length);
                } else if (currentOutput.length >= beforeOutput.length) {
                    added = currentOutput.slice(-1500);
                }
                
                const lower = added.toLowerCase();
                const hasError = lower.includes('error') || lower.includes('failed') || lower.includes('exception') || lower.includes('command not found') || lower.includes('econnrefused') || lower.includes('cannot find module');
                
                if (hasError) {
                    autoRetriesRef.current += 1;
                    await handleAiSendInner("I got an error in the output after running those commands. Please fix it automatically. Output:\n" + added.slice(-1000), true);
                } else if (!isComplete) {
                    autoRetriesRef.current += 1;
                    await handleAiSendInner("Here is the output from the last commands. Continue the next step of the task:\n" + added.slice(-1000), true);
                }
            } else if (!isComplete && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                await new Promise(r => setTimeout(r, 2000));
                if (autoRetriesRef.current < 6) {
                    autoRetriesRef.current += 1;
                    await handleAiSendInner("No commands were executed. Provide the next commands to complete the task.", true);
                }
            }
        } catch (err) {
            setAiMessages(prev => [...prev, { role: 'error', content: err.message }]);
        } finally { 
            // Only toggle loading off if this is the end of the recursion chain
            if (!isAutoRetry) {
                setAiLoading(false);
            }
        }
    };

    const handleAiSend = () => {
        setAiLoading(true);
        handleAiSendInner().catch(() => setAiLoading(false));
    };

    const getLoadingStage = () => {
        switch (connectionState) {
            case 'initializing': return { step: 1, label: 'Creating session', hint: 'Allocating a terminal session on the server...' };
            case 'connecting': return { step: 2, label: 'Connecting', hint: 'Opening WebSocket to the container...' };
            case 'reconnecting': return { step: 2, label: `Reconnecting (${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`, hint: 'Lost connection — retrying with exponential backoff...' };
            default: return null;
        }
    };

    const getConnectionInfo = () => {
        switch (connectionState) {
            case 'initializing': return { label: 'Initializing', color: '#d7af5f', icon: null };
            case 'connecting': return { label: 'Connecting', color: '#d7af5f', icon: null };
            case 'connected': return { label: latency ? `Connected (${latency}ms)` : 'Connected', color: '#4ec9b0', icon: Wifi };
            case 'reconnecting': return { label: `Reconnecting (${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`, color: '#d7af5f', icon: RefreshCw };
            case 'disconnected': return { label: 'Disconnected', color: '#d75f5f', icon: WifiOff };
            case 'error': return { label: 'Error', color: '#d75f5f', icon: WifiOff };
            default: return { label: 'Unknown', color: '#888', icon: null };
        }
    };

    const connInfo = getConnectionInfo();
    const loadingStage = getLoadingStage();
    const terminalReady = connectionState === 'connected';
    const isReconnecting = connectionState === 'reconnecting';

    return (
        <div className={`container-terminal ${embedded ? 'embedded' : ''}`} style={embedded ? { border: 'none', borderRadius: 0, boxShadow: 'none' } : {}}>
            <div className="terminal-header">
                <div className="terminal-title">
                    <TerminalIcon size={18} />
                    <span>{appName}</span>
                    <div className="terminal-status-badge" style={{ color: connInfo.color }}>
                        <div className={`status-indicator ${terminalReady ? 'connected' : 'disconnected'}`} />
                        <span className="status-label">{connInfo.label}</span>
                    </div>
                </div>
                <div className="terminal-actions">
                    {(connectionState === 'disconnected' || connectionState === 'error') && (
                        <button onClick={handleReconnect} title="Reconnect" className="terminal-button reconnect-button"><RefreshCw size={16} /></button>
                    )}
                    <input ref={fileInputRef} type="file" accept=".zip" style={{ display: 'none' }} onChange={handleZipUpload} />
                    <button onClick={() => fileInputRef.current?.click()} title="Upload ZIP to container" className={`terminal-button${uploading ? ' uploading' : ''}`} disabled={!terminalReady || uploading}>
                        {uploading ? <span className="terminal-upload-spinner" /> : <Upload size={16} />}
                    </button>
                    {onOpenFileManager && (
                        <button onClick={onOpenFileManager} title="File Manager" className="terminal-button">
                            <FolderTree size={16} />
                        </button>
                    )}
                    <button onClick={() => setShowAi(prev => !prev)} title="AI Assistant" className={`terminal-button ai-toggle${showAi ? ' active' : ''}`}>
                        <Sparkles size={16} />
                    </button>
                    <button onClick={handleCopy} title="Copy selection" className="terminal-button" disabled={!terminalReady}><Copy size={16} /></button>
                    <button onClick={handleClear} title="Clear terminal" className="terminal-button" disabled={!terminalReady}><RotateCw size={16} /></button>
                    {!embedded && (
                        <button onClick={onClose} title="Close terminal" className="terminal-button close-button"><X size={16} /></button>
                    )}
                </div>
            </div>

            <div className={showAi ? 'terminal-with-ai' : ''} style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
                <div className="terminal-body">
                    <ErrorBoundary>
                        <div ref={terminalRef} className="xterm-container" style={{ opacity: terminalReady || isReconnecting ? 1 : 0, pointerEvents: terminalReady ? 'auto' : 'none', height: '100%', transition: 'opacity 0.3s ease' }} />
                    </ErrorBoundary>

                    {loadingStage && !error && (
                        <div className="terminal-loading">
                            <div className="terminal-loading-content">
                                <div className="terminal-stage-steps">
                                    {[1, 2, 3].map(n => (
                                        <div key={n} className={`terminal-stage-step ${n < loadingStage.step ? 'done' : n === loadingStage.step ? 'active' : 'pending'}`}>
                                            <div className="terminal-stage-dot">{n < loadingStage.step ? '✓' : n}</div>
                                            <span className="terminal-stage-label">{n === 1 ? 'Session' : n === 2 ? 'Connect' : 'Ready'}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="terminal-spinner" style={{ marginTop: '1.25rem' }} />
                                <p><strong>{loadingStage.label}</strong></p>
                                <span className="terminal-loading-hint">{loadingStage.hint}</span>
                            </div>
                        </div>
                    )}

                    {isReconnecting && !error && (
                        <div className="terminal-reconnect-overlay">
                            <div className="terminal-reconnect-pill">
                                <span className="terminal-upload-spinner" style={{ borderTopColor: '#d7af5f', borderColor: 'rgba(215,175,95,0.3)' }} />
                                Reconnecting ({reconnectAttemptsRef.current}/{MAX_RECONNECT_ATTEMPTS})...
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="terminal-error">
                            <div className="terminal-error-content">
                                <WifiOff size={32} />
                                <p>{error}</p>
                                <div className="terminal-error-actions">
                                    <button onClick={handleReconnect} className="terminal-retry-btn"><RefreshCw size={16} /> Reconnect</button>
                                    <button onClick={onClose} className="terminal-close-btn">Close</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {showAi && (
                    <div className="terminal-ai-panel">
                        <div className="ai-panel-header">
                            <div className="ai-panel-title"><Sparkles size={16} /> AI Assistant</div>
                            <button className="ai-panel-close" onClick={() => setShowAi(false)}><X size={16} /></button>
                        </div>
                        <div className="ai-messages">
                            {aiMessages.length === 0 && !aiLoading && (
                                <div className="ai-msg-welcome">
                                    <div className="ai-welcome-icon">✨</div>
                                    <div>Ask me anything about your container, commands, debugging, or DevOps.</div>
                                </div>
                            )}
                            {aiMessages.map((msg, i) => (
                                <div key={i} className={`ai-msg ${msg.role}`}>
                                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.content}</div>
                                    {msg.commands && msg.commands.length > 0 && (
                                        <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            {msg.commands.map((cmd, j) => (
                                                <div key={j} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(139, 92, 246, 0.15)', padding: '4px 8px', borderRadius: '4px', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
                                                    <code style={{ fontSize: '11px', color: '#c4b5fd', fontFamily: 'monospace', wordBreak: 'break-all' }}>{cmd}</code>
                                                    <button onClick={() => runCommandInTerminal(cmd)} style={{ background: '#6d28d9', border: 'none', borderRadius: '4px', color: '#fff', fontSize: '10px', padding: '2px 6px', cursor: 'pointer', marginLeft: '6px' }}>Run</button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                            {aiLoading && (
                                <div className="ai-typing"><span /><span /><span /></div>
                            )}
                            <div ref={aiMessagesEndRef} />
                        </div>
                        <div className="ai-input-area">
                            <input
                                type="text"
                                value={aiInput}
                                onChange={e => setAiInput(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAiSend(); } }}
                                placeholder="Ask AI..."
                                disabled={aiLoading}
                            />
                            <button className="ai-send-btn" onClick={handleAiSend} disabled={!aiInput.trim() || aiLoading}>
                                <Send size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContainerTerminal;
